import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  MapPin,
  Armchair,
  Ticket,
  Hash,
  Settings2,
  ChevronRight,
} from "lucide-react";
import {
  agruparComprasPorEvento,
  getBoletosByCompra,
  hidratarComprasBoletos,
} from "@/data/comprasStore";
import { getCurrentSocio } from "@/data/socioAuthStore";
import BoletoDetalleFila from "@/components/common/BoletoDetalleFila";
import { DETALLE_COMPRAS } from "@/constants/testIds";
import { formatearFechaLarga } from "@/utils/fechas";

/**
 * Sprint 54 · Fase A · Desglose por compra de un evento.
 * Cada compra conserva su historial individual: mesa, salón, cantidad,
 * total y referencia de pago. Las compras NO se fusionan.
 */
export default function DetalleEventoCompras() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const socio = useMemo(() => getCurrentSocio(), []);
  const [tick, setTick] = useState(0);

  // Ruta actual (para que Asignar sepa a dónde regresar) y destino del
  // botón "Regresar" (origen inmediato con fallback seguro a Mis Boletos).
  const rutaActual = `/app/eventos/${eventoId}/mis-compras`;
  const volverA = location.state?.from || "/app/mis-boletos";

  useEffect(() => {
    hidratarComprasBoletos().then(() => setTick((t) => t + 1));
  }, []);

  const grupo = useMemo(() => {
    if (!socio) return null;
    return (
      agruparComprasPorEvento(socio.id).find((g) => g.eventoId === eventoId) ||
      null
    );
  }, [socio, eventoId, tick]);

  if (!socio) return <Navigate to="/login" replace />;
  if (!grupo) return <Navigate to="/app/mis-boletos" replace />;

  const fmt = (n) => `$${Number(n).toLocaleString("es-MX")} MXN`;

  return (
    <div
      data-testid={DETALLE_COMPRAS.screen}
      className="flex flex-col gap-6 pb-8"
    >
      <button
        type="button"
        data-testid={DETALLE_COMPRAS.btnRegresar}
        onClick={() => navigate(volverA)}
        aria-label="Regresar"
        className="self-start inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
      >
        <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
        </span>
        Regresar
      </button>

      <header className="animate-fade-up">
        <span className="overline text-accent">Detalle del evento</span>
        <h1
          data-testid={DETALLE_COMPRAS.nombre}
          className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground"
        >
          {grupo.nombreEvento}
        </h1>
        <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <li className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{formatearFechaLarga(grupo.fechaEvento)}</span>
          </li>
          <li className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{grupo.horaEvento}</span>
          </li>
        </ul>
      </header>

      {/* Resumen acumulado */}
      <section className="rounded-2xl border hairline bg-card p-5 grid grid-cols-2 gap-3 text-center">
        <div>
          <div
            data-testid={DETALLE_COMPRAS.totalBoletos}
            className="font-heading text-2xl text-foreground"
          >
            {grupo.totalBoletos}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
            boletos en total
          </div>
        </div>
        <div className="border-l hairline">
          <div
            data-testid={DETALLE_COMPRAS.totalPagado}
            className="font-heading text-2xl text-primary dark:text-accent"
          >
            {fmt(grupo.totalPagado)}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
            pagado
          </div>
        </div>
      </section>

      {/* Desglose por compra */}
      <ul
        data-testid={DETALLE_COMPRAS.list}
        className="flex flex-col gap-3"
      >
        {grupo.compras.map((c) => {
          const bs = getBoletosByCompra(c.id);
          const asignados = bs.filter(
            (b) => (b.asistenteNombre || "").trim().length > 0,
          ).length;
          const pendientes = bs.length - asignados;
          const referencia = c.referencia || c.pagoId || "—";
          return (
            <li
              key={c.id}
              data-testid={DETALLE_COMPRAS.item(c.id)}
              className="rounded-2xl border hairline bg-card p-5 shadow-soft flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Hash className="w-3.5 h-3.5 text-accent" strokeWidth={1.8} />
                  <span
                    data-testid={DETALLE_COMPRAS.itemReferencia(c.id)}
                    className="font-mono"
                  >
                    {referencia}
                  </span>
                </div>
                <span
                  data-testid={DETALLE_COMPRAS.itemTotal(c.id)}
                  className="font-heading text-lg text-primary dark:text-accent"
                >
                  {fmt(c.total)}
                </span>
              </div>

              <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <li className="flex flex-col">
                  <span className="overline text-muted-foreground">Salón</span>
                  <span
                    data-testid={DETALLE_COMPRAS.itemSalon(c.id)}
                    className="text-foreground mt-1 flex items-center gap-1.5"
                  >
                    <MapPin className="w-3.5 h-3.5 text-accent" strokeWidth={1.6} />
                    {c.salon || (Array.isArray(c.items) && c.items.length ? "Acceso general" : "—")}
                  </span>
                </li>
                {c.mesa !== null && c.mesa !== undefined && (
                  <li className="flex flex-col">
                    <span className="overline text-muted-foreground">Mesa</span>
                    <span
                      data-testid={DETALLE_COMPRAS.itemMesa(c.id)}
                      className="text-foreground mt-1 flex items-center gap-1.5"
                    >
                      <Armchair className="w-3.5 h-3.5 text-accent" strokeWidth={1.6} />
                      {c.mesa}
                    </span>
                  </li>
                )}
                <li className="flex flex-col">
                  <span className="overline text-muted-foreground">Boletos</span>
                  <span
                    data-testid={DETALLE_COMPRAS.itemCantidad(c.id)}
                    className="text-foreground mt-1 flex items-center gap-1.5"
                  >
                    <Ticket className="w-3.5 h-3.5 text-accent" strokeWidth={1.6} />
                    {c.cantidad}
                  </span>
                </li>
                <li className="flex flex-col">
                  <span className="overline text-muted-foreground">Estado</span>
                  <span className="text-foreground mt-1">
                    {asignados}/{bs.length} asignados
                  </span>
                </li>
              </ul>

              {Array.isArray(c.items) && c.items.length > 0 && (
                <ul
                  data-testid={`detalle-compras-items-${c.id}`}
                  className="mt-1 flex flex-col gap-1.5 rounded-xl border hairline bg-muted/20 p-3"
                >
                  <li className="overline text-muted-foreground">Desglose por salón</li>
                  {c.items.map((it) => (
                    <li
                      key={it.id || it.salonAccesoId}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span className="text-muted-foreground min-w-0 truncate">
                        {it.salon} · {it.cantidad} × ${Number(it.precioPorBoleto).toLocaleString("es-MX")}
                      </span>
                      <span className="text-foreground tabular-nums whitespace-nowrap">
                        ${Number(it.subtotal).toLocaleString("es-MX")} MXN
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {bs.length > 0 && (
                <ul
                  data-testid={DETALLE_COMPRAS.boletos(c.id)}
                  className="divide-y divide-border rounded-xl border hairline px-3"
                >
                  {bs.map((b, idx) => (
                    <BoletoDetalleFila key={b.id} boleto={b} numero={idx + 1} />
                  ))}
                </ul>
              )}

              <button
                type="button"
                data-testid={DETALLE_COMPRAS.itemAsignar(c.id)}
                onClick={() =>
                  navigate(`/app/asignar/${c.id}`, {
                    state: { from: rutaActual },
                  })
                }
                className={
                  "self-start inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium border hairline transition-colors " +
                  (pendientes > 0
                    ? "bg-primary/10 dark:bg-accent/10 text-primary dark:text-accent border-primary/30 dark:border-accent/40 hover:bg-primary/20"
                    : "bg-card text-foreground hover:bg-muted")
                }
              >
                {pendientes > 0
                  ? `Asignar (${pendientes} pendientes)`
                  : "Ver boletos"}
                <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        data-testid={DETALLE_COMPRAS.btnAdministrarTodo}
        onClick={() =>
          navigate(`/app/eventos/${eventoId}/asignar`, {
            state: { from: rutaActual },
          })
        }
        className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-wide bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft transition-all duration-200 active:scale-[0.99] hover:opacity-95"
      >
        <Settings2 className="w-4 h-4" strokeWidth={1.8} />
        Administrar todos los boletos
      </button>
    </div>
  );
}
